import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useAgentChat() {
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [pendingBatches, setPendingBatches] = useState([]);
  const abortControllerRef = useRef(null);
  const pollingRef = useRef(null);

  // Polling de batches pendentes
  useEffect(() => {
    if (pendingBatches.length === 0) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    pollingRef.current = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      for (const batch of pendingBatches) {
        try {
          const response = await fetch(`${supabaseUrl}/functions/v1/check-batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              batch_id: batch.batchId,
              message_id: batch.messageId,
            }),
          });

          if (!response.ok) continue;

          const data = await response.json();

          if (data.status === 'completed') {
            // Remover do pendingBatches
            setPendingBatches(prev => prev.filter(b => b.batchId !== batch.batchId));
            // Chamar callback de conclusao
            batch.onComplete?.(data.content);
          }
        } catch (err) {
          console.warn('Erro ao verificar batch:', err);
        }
      }
    }, 30000); // Verificar a cada 30 segundos

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [pendingBatches]);

  // Enviar mensagem (urgente = streaming, normal = batch)
  const sendMessage = useCallback(async ({
    message,
    agentId,
    conversationId,
    clientId = null,
    urgent = true,
    onChunk,
    onComplete,
    onError,
    onBatchSubmitted,
  }) => {
    setSending(true);
    setStreamingContent('');
    abortControllerRef.current = new AbortController();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Nao autenticado');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/agent-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          message,
          agent_id: agentId,
          conversation_id: conversationId,
          client_id: clientId,
          urgent,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erro ${response.status}`);
      }

      // ---- MODO BATCH ----
      if (!urgent) {
        const data = await response.json();
        setSending(false);

        // Registrar para polling
        const batchEntry = {
          batchId: data.batch_id,
          messageId: null, // Sera preenchido quando achar no banco
          conversationId: data.conversation_id,
          onComplete,
        };

        // Buscar o message_id da mensagem placeholder
        const { data: msgs } = await supabase
          .from('agency_messages')
          .select('id')
          .eq('conversation_id', data.conversation_id)
          .eq('role', 'assistant')
          .order('created_at', { ascending: false })
          .limit(1);

        if (msgs && msgs.length > 0) {
          batchEntry.messageId = msgs[0].id;
        }

        setPendingBatches(prev => [...prev, batchEntry]);
        onBatchSubmitted?.(data);
        return;
      }

      // ---- MODO URGENTE (streaming) ----
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);

          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'content') {
              fullContent += parsed.text;
              setStreamingContent(fullContent);
              onChunk?.(parsed.text, fullContent);
            } else if (parsed.type === 'progress') {
              onChunk?.(null, fullContent, parsed.status);
            } else if (parsed.type === 'error') {
              throw new Error(parsed.message);
            }
          } catch (parseErr) {
            if (parseErr.message !== 'Unexpected end of JSON input') {
              console.warn('Erro ao parsear SSE:', parseErr);
            }
          }
        }
      }

      setSending(false);
      setStreamingContent('');
      onComplete?.(fullContent);
    } catch (err) {
      setSending(false);
      setStreamingContent('');
      if (err.name !== 'AbortError') {
        console.error('Erro no chat:', err);
        onError?.(err.message);
      }
    }
  }, []);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setSending(false);
    setStreamingContent('');
  }, []);

  return {
    sendMessage,
    cancelStream,
    sending,
    streamingContent,
    pendingBatches,
  };
}
